import { PanDirective } from '@23degrees/directives/pan.directive';
import { MessageBus } from '@23degrees/shared/bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';
import { GlobalService } from '@23degrees/shared/services/global';
import { debounce } from '@23degrees/utilities/debounce.utils';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { MESSAGE_RECIPIENTS, REQUEST_MESSAGES } from '@config';

@Component({
  selector: 'timeline-control',
  templateUrl: 'timeline-control.component.html',
  styleUrls: ['./timeline-control.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PanDirective],
})
export class TimelineControlComponent extends BaseClass {
  private cdr = inject(ChangeDetectorRef);
  renderer = inject(Renderer2);
  elemRef = inject(ElementRef);
  messageBus = inject(MessageBus);

  @ViewChild('tabsContainer') tabsContainer: ElementRef;
  @ViewChild('tabsWrapper') tabsWrapper: ElementRef;
  @ViewChildren('tabs') tabs: QueryList<ElementRef>;

  @Input() items: Array<any>;
  @Input() selected: any;
  @Input() style: any;

  @Output() selectedChange = new EventEmitter();

  private viewInit: boolean = false;

  globalService: GlobalService = inject(GlobalService);

  selectedItem: any;

  nativeTabbar: any = this.elemRef.nativeElement;
  activeSwipePosition: number = 0;
  currentSwipePosition: number = 0;
  swipeActive: boolean = false;

  oversized: boolean = false;
  prevLinkInactive: boolean = false;
  nextLinkInactive: boolean = false;

  /**
   * Changes is always triggering when
   * new value is emittied
   */
  ngOnChanges(changes: SimpleChanges) {
    // console.log('[TimelineControl] [SWIPE TABS] OnChangesEvent', changes);
    this.selectedItem = this.selected;
    if (changes.items || changes.selected) {
      // console.log('[SWIPE TABS]', 'onChnages');
      this.swipTabs();
      this.cdr.detectChanges();
      return;
    }
    if (changes.style) {
      if (!this.compareObjects(changes.style.currentValue, changes.style.previousValue)) {
        // console.log('[SWIPE TABS]', 'StyleChnages');
        this.swipTabs();
        this.cdr.detectChanges();
      }
    }
  }

  ngOnInit() {
    this.messageBus.on('RENDER_FINISH', MESSAGE_RECIPIENTS.Filter, this.onMessageBusEvent, this.componentDestroyed$);
    this.messageBus.on(REQUEST_MESSAGES.ContentResize, MESSAGE_RECIPIENTS.Map, this.onMessageBusEvent, this.componentDestroyed$);
    this.messageBus.on(REQUEST_MESSAGES.ContentResize, MESSAGE_RECIPIENTS.Chart, this.onMessageBusEvent, this.componentDestroyed$);
  }

  ngAfterViewInit() {
    this.viewInit = true;
    this.cdr.detach();
    this.initView();
    // console.log('[SWIPE TABS]', 'onViewInit');
    this.swipTabs();
    this.cdr.detectChanges();
  }

  initView() {
    this.oversized = this.tabsContainer.nativeElement.clientWidth > this.tabsWrapper.nativeElement.clientWidth / 2;
    // this.cdr.detectChanges();
  }

  swipTabs() {
    if (this.isBrowser()) {
      try {
        if (!this.viewInit) return;
        const tabWrapper_width = this.tabsWrapper.nativeElement.clientWidth;
        const tabBar = this.tabsContainer.nativeElement;
        const selectedTab = tabBar.querySelector('.active');
        const selectedTab_Width = selectedTab.clientWidth;
        const selectedTab_LeftOffset = selectedTab.getBoundingClientRect().left - tabBar.getBoundingClientRect().left;
        const selectedTab_mid = selectedTab_LeftOffset + selectedTab_Width / 2;

        const newPositon = tabWrapper_width / 2 - selectedTab_mid;
        this.setTabsPosition(newPositon);
      } catch (error) {
        console.error('Size could not get evaluated on timeline!', this.selectedItem, error);
      }
    }
  }

  selectItem(value) {
    console.log('SELECT ITEM', value);
    if (this.swipeActive) return;
    this.selectedItem = value;
    this.selectedChange.emit(this.selectedItem);
    this.cdr.detectChanges();
    // console.log('[SWIPE TABS]', 'selectItem');
    this.swipTabs();
    this.cdr.detectChanges();
  }

  paging(forward) {
    this.prevLinkInactive = this.nextLinkInactive = false;
    const newPosition = forward
      ? this.activeSwipePosition + this.tabsWrapper.nativeElement.clientWidth
      : this.activeSwipePosition - this.tabsWrapper.nativeElement.clientWidth;

    // console.log('[TimelineControl] paging - forward: ', forward, ' newPosition: ', newPosition);
    this.setTabsPosition(newPosition);
  }

  panStart() {
    this.swipeActive = true;
    this.setTabsTransition(false);
  }

  pan(event) {
    this.currentSwipePosition = this.activeSwipePosition + event.deltaX;
    this.setTabsPosition(this.currentSwipePosition, false);
  }

  panEnd(event) {
    this.setTabsTransition(true);
    this.activeSwipePosition = this.currentSwipePosition;
    this.cdr.detectChanges();
    // This is for click is not fireing when drag stops
    setTimeout(() => {
      this.swipeActive = false;
    });
  }

  setTabsPosition(xPosition, setActive: boolean = true) {
    this.prevLinkInactive = this.nextLinkInactive = false;

    const container = this.tabsContainer.nativeElement;
    const wrapperWidth = this.tabsWrapper.nativeElement.clientWidth;
    const tabsWidth = this.tabsContainer.nativeElement.clientWidth;
    const tabItems = this.tabs.toArray();
    const firstItem = tabItems[0].nativeElement;
    const lastItem = tabItems[tabItems.length - 1].nativeElement;
    const max = wrapperWidth / 2 - firstItem.clientWidth / 2;
    const min = wrapperWidth / 2 - tabsWidth + lastItem.clientWidth / 2;

    if (xPosition >= max) {
      xPosition = max;
      this.nextLinkInactive = true;
    }

    if (xPosition <= min) {
      xPosition = min;
      this.prevLinkInactive = true;
    }

    if (setActive) {
      this.activeSwipePosition = xPosition;
    }

    this.renderer.setStyle(container, '-webkit-transform', `translateX(${xPosition}px)`);
    this.renderer.setStyle(container, '-moz-transform', `translateX(${xPosition}px)`);
    this.renderer.setStyle(container, 'transform', `translateX(${xPosition}px)`);
    // console.log('[TimelineControl] setTabPosition - xPosition: ', xPosition, ' setActive: ', setActive, 'timing: ', new Date().getTime());
  }

  setTabsTransition(active: boolean = true) {
    const time = active ? 13 : 0;
    const container = this.tabsContainer.nativeElement;

    this.renderer.setStyle(container, '-webkit-transition', `transform .${time}s ease-out`);
    this.renderer.setStyle(container, '-moz-transition', `transform .${time}s ease-out`);
    this.renderer.setStyle(container, 'transition', `transform .${time}s ease-out`);
  }

  onMessageBusEvent = (msg) => {
    // console.log('[TimelineControl][onMessageBusEvent]', msg);
    this.oversized = this.tabsContainer.nativeElement.clientWidth > this.tabsWrapper.nativeElement.clientWidth / 2;
    // console.log('[SWIPE TABS]', 'onMessageBusEvent');
    this.swipTabs();
    this.cdr.detectChanges();
  };

  @HostListener('window:resize', ['$event'])
  @debounce(100)
  onResize(event) {
    // console.log('[TimelineControl][onResize]', event);
    this.oversized = this.tabsContainer.nativeElement.clientWidth > this.tabsWrapper.nativeElement.clientWidth / 2;
    this.swipTabs();
    this.cdr.detectChanges();
  }

  compareObjects = (o1, o2) => {
    if (!o1 || !o2) return false;

    for (const p in o1) {
      if (o1.hasOwnProperty(p)) {
        if (o1[p] !== o2[p]) {
          return false;
        }
      }
    }
    for (const p in o2) {
      if (o2.hasOwnProperty(p)) {
        if (o1[p] !== o2[p]) {
          return false;
        }
      }
    }
    return true;
  };
}
